import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import logger from "@/lib/logger";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Logo from "@/components/Logo";

export default function OnboardingPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(new Set(user?.interests || []));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/categories");
        setCategories(data.categories || []);
      } catch (err) {
        logger.warn("onboarding", "categories fetch failed", err);
      }
    })();
  }, []);

  const toggle = (c) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else if (next.size < 8) next.add(c);
      return next;
    });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.patch("/auth/me", {
        interests: Array.from(selected),
        onboarded: true,
      });
      setUser(data);
      toast.success("Welcome to Voxly");
      navigate("/feed", { replace: true });
    } catch (e) {
      toast.error("Couldn't save preferences");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="onboarding-page">
      <div className="mx-auto max-w-md min-h-screen sm:border-x sm:border-white/5 px-6 py-10">
        <div className="mb-8">
          <Logo size={40} />
        </div>

        <h1 className="font-heading text-4xl font-black tracking-tight">
          What voices<br/><span className="text-primary">move you?</span>
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Pick up to 8 topics. We'll tune your feed to the conversations you love.
        </p>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="categories-grid">
          {categories.map((c) => {
            const active = selected.has(c);
            return (
              <button
                key={c}
                data-testid={`category-${c.toLowerCase()}`}
                onClick={() => toggle(c)}
                className={`px-4 py-2.5 rounded-full border text-sm font-medium transition ${
                  active
                    ? "bg-primary text-primary-foreground border-primary glow"
                    : "border-white/15 hover:border-white/30 text-foreground/90"
                }`}
              >
                {c}
              </button>
            );
          })}
        </div>

        <div className="mt-2 text-xs text-muted-foreground font-mono">
          {selected.size}/8 selected
        </div>

        <div className="mt-10 flex flex-col gap-3">
          <button
            data-testid="onboarding-continue"
            onClick={submit}
            disabled={submitting}
            className="w-full rounded-full bg-primary text-primary-foreground py-4 text-base font-semibold flex items-center justify-center gap-2 glow-hover disabled:opacity-50"
          >
            {submitting ? "Saving…" : <>Continue <ArrowRight size={18} weight="bold" /></>}
          </button>
          <button
            data-testid="onboarding-skip"
            onClick={() => { setSelected(new Set()); submit(); }}
            className="text-sm text-muted-foreground hover:text-foreground py-2"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}
